import { Box, Stack } from "@mui/material";
import type { DataGridPremiumProps, GridApiPremium, GridColDef } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useMemo, useState } from "react";
import { isAutoGeneratedRowNode } from "@entrilia/shared/components/grid/gridHelper";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import useDebounce from "@entrilia/shared/hooks/useDebounce";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import type { OrganizationReportInfo } from "../../../../api/types/svTypes.ts";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import createCheckboxColumn from "../../../common/grid/createCheckboxColumn";
import { useReportsManagerContext } from "../../reporting/report-lists/contexts/reports-manager/ReportsManagerContext";
import { useReportsContext } from "../../reporting/report-lists/contexts/reports/ReportsContext";
import ReportMenuAction from "../../reporting/report-lists/reports/components/ReportMenuAction";
import type { GridKey } from "../../reporting/report-lists/storage/reportsGridExpandStorage";
import type { BiApiClient } from "../../reporting/sv/contexts/ApiClientProviderContext";
import { useBiApiClientProvider } from "../../reporting/sv/contexts/BiApiClientProviderContext.ts";
import CustomHeader from "./CustomHeader";
import {
  getSharingColumn,
  createdByColumn,
  getModifiedAtColumn,
  modifiedByColumn,
  reportTypeColumn,
} from "./grid/GridHelper";
import OrganizationReportsGridWrapper from "./grid/OrganizationReportsGridWrapper";

interface Props {
  pageTitle: string;
  gridKey: GridKey;
}
const OrganizationReportsPage = ({ pageTitle, gridKey }: Props) => {
  usePageTitle(pageTitle);

  const { copyReports, deleteReports, shareReport } = useReportsManagerContext();
  const { getViewReportUrl } = useBiApiClientProvider<BiApiClient>();
  const { reports, ui } = useReportsContext<OrganizationReportInfo>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");
  const [currentReport, setCurrentReport] = useState<OrganizationReportInfo>();

  const reportsToShow = useMemo(() => {
    if (!reports) return [];
    if (!search) return reports;
    return reports.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));
  }, [reports, search]);

  const handleSearch = useDebounce((value: string) => setSearch(value), 300);

  const selectedReports = useMemo(
    () => reports.filter((r) => selectedIds.includes(r.reportId)),
    [reports, selectedIds]
  );

  const getColumns = useCallback(
    (apiRef: RefObject<GridApiPremium | null>): GridColDef[] => {
      return [
        createCheckboxColumn(),
        getSharingColumn(shareReport),
        reportTypeColumn,
        createdByColumn,
        modifiedByColumn,
        getModifiedAtColumn(apiRef),
        {
          field: "actions",
          sortable: false,
          resizable: false,
          headerName: "Actions",
          width: 80,
          cellClassName: "grid-row-actions",
          align: "right",
          headerAlign: "right",
          renderCell: (params) => {
            if (isAutoGeneratedRowNode(params.rowNode)) {
              return null;
            }
            return (
              <ReportMenuAction
                report={params.row as OrganizationReportInfo}
                openInNewTab={true}
                onReportSelected={setCurrentReport}
              />
            );
          },
        },
      ];
    },
    [shareReport]
  );

  return (
    <Stack sx={{ flex: 1, width: "100%" }}>
      <GeneralPageHeader title="Reports" />
      <Stack spacing={2} useFlexGap sx={{ flex: 1, px: 3, py: 2.5, width: "100%" }}>
        <Box sx={{ ml: "auto" }}>
          <SearchField onSearch={handleSearch} />
        </Box>
        <ScrollableFlexContainer>
          <OrganizationReportsGridWrapper
            getViewReportUrl={getViewReportUrl}
            reports={reportsToShow}
            loading={!ui.loaded}
            error={undefined}
            getColumns={getColumns}
            getTreeDataPath={getTreeDataPath}
            Toolbar={
              <CustomHeader selectedReports={selectedReports} copyReports={copyReports} deleteReports={deleteReports} />
            }
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
            currentReport={currentReport}
            gridKey={gridKey}
          />
        </ScrollableFlexContainer>
      </Stack>
    </Stack>
  );
};

const getTreeDataPath: DataGridPremiumProps["getTreeDataPath"] = (row: OrganizationReportInfo) => [
  row.clientCode,
  row.groupName,
  row.reportId,
];

export default OrganizationReportsPage;
